/**
 * inlineEdit.ts — the PURE decisions behind Slice D / PR D3: inline edit of
 * owner and status IN a list (§15.15 D-4; the E-8 boundary — E owns edits ON
 * a record's page, D owns edits IN a list).
 *
 * FIELD SET (confirmed against main, not the recon): owner + status on the
 * Contacts list, owner + status on the Inbound Leads queue. NOT Accounts —
 * D-4 and E-8 both say "owner on Accounts", but sales_accounts (0116) has no
 * owner column, UpdateAccountRequest carries none and accountChanges tracks
 * none, so an Accounts owner cell would have nothing honest to read or write.
 * Data honesty outranks the spec: no cell is drawn.
 *
 * WRITE PATH: the live PUT /v1/sales/contacts/{id} for both lists (a lead IS
 * a contact, L8-1) — partial body, set-only (empty = keep). No new route.
 * Every committed change writes a permanent sales_field_history row
 * (contactChanges tracks `status` and `assigned_employee_id`), which is the
 * point AND the risk: a mis-click is an audit row that cannot be removed, and
 * there is deliberately NO undo — a second row saying "changed back" is the
 * honest record. What keeps a wrong edit unlikely lives in these rules and
 * in InlinePicklist:
 *   - same value → NO request (statusChange / ownerChange return null);
 *   - the vocabulary is bounded (4 statuses; the queue's two exits; current
 *     employees only), so a wrong pick is a wrong-but-valid value, never junk
 *     — load-bearing because the server does not validate `status` at all;
 *   - the wire cannot CLEAR an owner (empty = keep), so no "Unassigned" action
 *     is offered — an option that silently did nothing would be a lie;
 *   - the toast names the record, old → new and that history recorded it, so
 *     a wrong edit is seen at once and corrected with a second honest row.
 *
 * No React, no network here; pinned by inlineEdit.test.ts.
 */
import type { SalesContact, SalesLead, ApiEmployeeDirectoryRow } from '../../types'

export type ContactStatus = SalesContact['status']

export interface PickOption {
  value: string
  label: string
}

/** The CRM-wide status vocabulary — the Contacts list offers all four. */
export const CONTACT_STATUS_OPTIONS: ReadonlyArray<{ value: ContactStatus; label: string }> = [
  { value: 'new',         label: 'New' },
  { value: 'contacted',   label: 'Contacted' },
  { value: 'qualified',   label: 'Qualified' },
  { value: 'unqualified', label: 'Unqualified' },
]

/** The Inbound Leads queue offers its own EXITS only — Contacted and
 *  Unqualified, exactly the two the lead detail page's actions write
 *  (updateLeadStatus) — plus the current New. `qualified` is what CONVERT
 *  writes when it mints the deal (§8.D); minting a "qualified" lead with no
 *  deal from a row menu would create a state no lead surface expects. */
export const LEAD_STATUS_OPTIONS: ReadonlyArray<{ value: ContactStatus; label: string }> =
  CONTACT_STATUS_OPTIONS.filter(o => o.value !== 'qualified')

export function statusLabel(status: string): string {
  return CONTACT_STATUS_OPTIONS.find(o => o.value === status)?.label ?? status
}

/** Same value → null: nothing is sent and no history row is written. */
export function statusChange(current: ContactStatus, next: string): { status: string } | null {
  if (next === '' || next === current) return null
  return { status: next }
}

/** Same owner → null. An empty `next` is never a request: the wire's empty
 *  means keep, so "clear" is not something this control can honestly offer. */
export function ownerChange(currentId: string | undefined, nextId: string): { assignedEmployeeId: string } | null {
  if (nextId === '' || nextId === (currentId ?? '')) return null
  return { assignedEmployeeId: nextId }
}

/** People a row can be assigned to: the tenant's CURRENT employees, leavers
 *  excluded (OwnerSelect's rule). null while the directory is loading or
 *  unavailable → an empty list; the picklist then says so and never falls
 *  back to free text. */
export function ownerOptions(rows: ReadonlyArray<Pick<ApiEmployeeDirectoryRow, 'id' | 'fullName' | 'status'>> | null | undefined): PickOption[] {
  return (rows ?? []).filter(e => e.status !== 'leaver').map(e => ({ value: e.id, label: e.fullName }))
}

/** What the closed control shows for a row's owner: the server-derived
 *  display name (the linked employee's current name, or the legacy text on
 *  a pre-E360-2 row), else 'Unassigned'. Never the id. */
export function ownerLabel(row: { assignedTo?: string }): string {
  return row.assignedTo && row.assignedTo !== '' ? row.assignedTo : 'Unassigned'
}

/** The CELL's text: the name, or the list's own empty-value mark ('—', the
 *  idiom every '—' cell on these lists already uses, and what the Leads
 *  "Assigned" column showed before D3). The word "Unassigned" is reserved
 *  for the toast, where there is room for it. */
export function ownerCellText(row: { assignedTo?: string }): string {
  return row.assignedTo && row.assignedTo !== '' ? row.assignedTo : '—'
}

/** Contacts (D3.4): after an edit, does the row still satisfy the active
 *  view's filters? Mirrors the page's predicate for the two SAVED filters;
 *  search is transient and deliberately not part of a view. */
export function matchesContactsFilters(
  row: Pick<SalesContact, 'status' | 'prospectSegment'>,
  filters: Record<string, string>,
): boolean {
  const status = filters.status ?? 'all'
  const segment = filters.segment ?? 'all'
  if (status !== 'all' && row.status !== status) return false
  if (segment !== 'all' && (row.prospectSegment ?? '') !== segment) return false
  return true
}

/** Inbound Leads: the queue predicate (L8-2) — status 'new' AND unconverted.
 *  A row that stops satisfying it has left the queue, whatever wrote it. */
export function inLeadsQueue(lead: Pick<SalesLead, 'status' | 'convertedDealId'>): boolean {
  return lead.status === 'new' && lead.convertedDealId === ''
}

/** The toast's first clause: who, which field, old → new. The labels are the
 *  values the SERVER returned, never the one that was clicked. */
export function changeSummary(name: string, field: 'status' | 'owner', from: string, to: string): string {
  return `${name}: ${field} ${from} → ${to}`
}
